import React from 'react';
import { FormControlLabel, IconButton, Typography } from '@mui/material';
import { IconUpload, IconDownload } from '@tabler/icons';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../auth';
import { useBasicAPIMutation } from '../../data/mutations';
import { getPluralEntity } from '../../utils/entities';
import { HealthStatus } from './EntityTable/entityTableCells/HealthStatus';
import { CatalogType } from './EntityTable/entityTableCells/CatalogType';
import { GreenSwitch } from './EntityTable/entityTableCells/GreenSwitch';
import { Entity } from '../../types/entity.types';

interface ColumnParams {
  row: any;
  value: unknown;
}

const getFlowsHealthStyles = (
  entity: any,
  direction: 'source' | 'destination',
): React.CSSProperties | null => {
  if (entity.aggregate) {
    if (entity.aggregate[`${direction}_unhealthy_count`] > 0) {
      return {
        color: '#ef5350',
        fontWeight: 'bold',
      };
    }
    if (entity.aggregate[`${direction}_healthy_count`] > 0) {
      return {
        color: '#4caf50',
        fontWeight: 'bold',
      };
    }
  }
  return null;
};

const EnabledToggleCell = ({
  entity,
  entityType,
}: {
  entity: Entity;
  entityType: string;
}) => {
  const { currentAccount } = useAccount();
  const pluralEntity = getPluralEntity(entityType)('entityHook');
  const mutation = useBasicAPIMutation(currentAccount.id, pluralEntity);

  return (
    <FormControlLabel
      control={
        <GreenSwitch
          checked={entity.enabled}
          size="small"
          onChange={() => {
            const updatedEntity = { ...entity, enabled: !entity.enabled };
            mutation.mutate(updatedEntity as any);
          }}
        />
      }
      label="Enabled"
    />
  );
};

const EditButtonCell = ({
  entity,
  entityType,
}: {
  entity: Entity;
  entityType: string;
}) => {
  const navigate = useNavigate();
  const { currentAccount } = useAccount();

  return (
    <IconButton
      aria-label="edit connector"
      onClick={() => {
        const typeName =
          entity['type_name'] ||
          (entity[`${entityType}-type`] as { name: string })?.name;
        navigate(
          `/${currentAccount.name}/${getPluralEntity(entityType)()}/configure/${typeName}/${entity.name}`,
        );
      }}
    >
      <ModeEditIcon />
    </IconButton>
  );
};

const getEntityListColumns = (entityType: string) => {
  const columns = [
    {
      field: 'health',
      headerName: 'Health',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params: ColumnParams) => (
        <HealthStatus entity={params.row} />
      ),
    },
    {
      field: 'catalogType',
      headerName: 'Type',
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: ColumnParams) => (
        <CatalogType entityType={params.row.catalogType} />
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: true,
    },
    {
      field: 'flows',
      headerName: 'Flows',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: ColumnParams) => {
        const sourceStyles = getFlowsHealthStyles(params.row, 'source') || {};
        const destStyles =
          getFlowsHealthStyles(params.row, 'destination') || {};
        const outbound = params.row.outboundFlowsCount ?? 0;
        const inbound = params.row.inboundFlowsCount ?? 0;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span
              style={{ display: 'flex', alignItems: 'center', fontSize: 16 }}
            >
              <IconUpload
                style={{
                  height: 18,
                  marginRight: 4,
                  transform: 'rotate(90deg)',
                }}
              />
              <span style={sourceStyles}>
                {outbound.toString().padStart(2, '0')}
              </span>
            </span>
            <span
              style={{ display: 'flex', alignItems: 'center', fontSize: 16 }}
            >
              <IconDownload
                style={{
                  height: 18,
                  marginRight: 4,
                  transform: 'rotate(90deg)',
                }}
              />
              <span style={destStyles}>
                {inbound.toString().padStart(2, '0')}
              </span>
            </span>
          </div>
        );
      },
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: ColumnParams) => (
        <EnabledToggleCell entity={params.row} entityType={entityType} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: ColumnParams) => (
        <EditButtonCell entity={params.row} entityType={entityType} />
      ),
    },
  ];

  return columns;
};

export default getEntityListColumns;
